import { EventReport, formatDate, formatDateTime, stationMethodColumns } from './event-report.model';

/**
 * Libro de Excel (.xlsx) del informe, generado a mano: un .xlsx es un zip
 * con XML adentro y el proyecto no trae ninguna librería de hojas de
 * cálculo, así que aquí va un escritor mínimo de zip (sin comprimir) y las
 * partes de SpreadsheetML que Excel, Numbers y Sheets necesitan.
 * Funciones puras: no tocan el DOM.
 */

// ═══════════════════════════════════════════════════════════════════════════
//  ZIP (método "store")
// ═══════════════════════════════════════════════════════════════════════════

let CRC_TABLE: Uint32Array = null;

function crcTable(): Uint32Array {
    if (CRC_TABLE) return CRC_TABLE;
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        table[n] = c >>> 0;
    }
    CRC_TABLE = table;
    return table;
}

function crc32(bytes: Uint8Array): number {
    const table = crcTable();
    let crc = 0xFFFFFFFF;
    for (let i = 0; i < bytes.length; i++) {
        crc = table[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
    }
    return (crc ^ 0xFFFFFFFF) >>> 0;
}

function utf8(text: string): Uint8Array {
    return new TextEncoder().encode(text);
}

/** Fecha/hora en el formato de MS-DOS que usan las cabeceras zip. */
function dosTime(d: Date): { time: number; date: number } {
    const time = (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2);
    const date = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
    return { time, date };
}

export function zipStore(files: { name: string; data: Uint8Array }[], when: Date = new Date()): Uint8Array {
    const { time, date } = dosTime(when);
    const locals: Uint8Array[] = [];
    const centrals: Uint8Array[] = [];
    let offset = 0;

    files.forEach(file => {
        const name = utf8(file.name);
        const crc = crc32(file.data);
        const size = file.data.length;

        // Cabecera local + nombre + datos.
        const local = new Uint8Array(30 + name.length + size);
        const lv = new DataView(local.buffer);
        lv.setUint32(0, 0x04034b50, true);
        lv.setUint16(4, 20, true);        // versión necesaria
        lv.setUint16(6, 0x0800, true);    // nombres en UTF-8
        lv.setUint16(8, 0, true);         // sin compresión
        lv.setUint16(10, time, true);
        lv.setUint16(12, date, true);
        lv.setUint32(14, crc, true);
        lv.setUint32(18, size, true);
        lv.setUint32(22, size, true);
        lv.setUint16(26, name.length, true);
        lv.setUint16(28, 0, true);
        local.set(name, 30);
        local.set(file.data, 30 + name.length);
        locals.push(local);

        // Entrada del directorio central.
        const central = new Uint8Array(46 + name.length);
        const cv = new DataView(central.buffer);
        cv.setUint32(0, 0x02014b50, true);
        cv.setUint16(4, 20, true);        // creada con
        cv.setUint16(6, 20, true);        // versión necesaria
        cv.setUint16(8, 0x0800, true);
        cv.setUint16(10, 0, true);
        cv.setUint16(12, time, true);
        cv.setUint16(14, date, true);
        cv.setUint32(16, crc, true);
        cv.setUint32(20, size, true);
        cv.setUint32(24, size, true);
        cv.setUint16(28, name.length, true);
        cv.setUint16(30, 0, true);        // extra
        cv.setUint16(32, 0, true);        // comentario
        cv.setUint16(34, 0, true);        // disco
        cv.setUint16(36, 0, true);        // atributos internos
        cv.setUint32(38, 0, true);        // atributos externos
        cv.setUint32(42, offset, true);   // offset de la cabecera local
        central.set(name, 46);
        centrals.push(central);

        offset += local.length;
    });

    const centralSize = centrals.reduce((sum, c) => sum + c.length, 0);
    const end = new Uint8Array(22);
    const ev = new DataView(end.buffer);
    ev.setUint32(0, 0x06054b50, true);
    ev.setUint16(4, 0, true);
    ev.setUint16(6, 0, true);
    ev.setUint16(8, files.length, true);
    ev.setUint16(10, files.length, true);
    ev.setUint32(12, centralSize, true);
    ev.setUint32(16, offset, true);
    ev.setUint16(20, 0, true);

    const total = offset + centralSize + end.length;
    const out = new Uint8Array(total);
    let pos = 0;
    locals.forEach(l => { out.set(l, pos); pos += l.length; });
    centrals.forEach(c => { out.set(c, pos); pos += c.length; });
    out.set(end, pos);
    return out;
}

// ═══════════════════════════════════════════════════════════════════════════
//  XLSX mínimo
// ═══════════════════════════════════════════════════════════════════════════

/** Índices de estilo en styles.xml (cellXfs). */
export const XS = {
    normal: 0,
    bold: 1,
    header: 2,
    money: 3,
    moneyBold: 4,
    title: 5,
    percent: 6,
    muted: 7,
    int: 8,
    intBold: 9,
};

export interface XCell {
    v: string | number | null;
    s?: number;
}

export interface XSheet {
    name: string;
    /** Anchos de columna en caracteres. */
    cols?: number[];
    rows: XCell[][];
    /** Fila (1-based) hasta la que se congela la vista, p. ej. la de cabeceras. */
    freezeRow?: number;
}

function xmlEscape(text: string): string {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
        // Caracteres de control que XML 1.0 no admite.
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');
}

function columnName(index: number): string {
    let name = '';
    let n = index + 1;
    while (n > 0) {
        const rem = (n - 1) % 26;
        name = String.fromCharCode(65 + rem) + name;
        n = Math.floor((n - 1) / 26);
    }
    return name;
}

function sheetName(name: string): string {
    return String(name || 'Hoja').replace(/[\[\]:*?\/\\]/g, ' ').trim().slice(0, 31) || 'Hoja';
}

function sheetXml(sheet: XSheet): string {
    const cols = (sheet.cols || []).map((w, i) =>
        `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('');

    const rows = sheet.rows.map((row, r) => {
        const cells = row.map((cell, c) => {
            if (!cell || cell.v === null || cell.v === undefined || cell.v === '') {
                return cell && cell.s ? `<c r="${columnName(c)}${r + 1}" s="${cell.s}"/>` : '';
            }
            const ref = `${columnName(c)}${r + 1}`;
            const style = cell.s ? ` s="${cell.s}"` : '';
            if (typeof cell.v === 'number' && isFinite(cell.v)) {
                return `<c r="${ref}"${style}><v>${cell.v}</v></c>`;
            }
            return `<c r="${ref}"${style} t="inlineStr"><is><t xml:space="preserve">${xmlEscape(String(cell.v))}</t></is></c>`;
        }).join('');
        return `<row r="${r + 1}">${cells}</row>`;
    }).join('');

    const freeze = sheet.freezeRow
        ? `<sheetViews><sheetView workbookViewId="0"><pane ySplit="${sheet.freezeRow}" topLeftCell="A${sheet.freezeRow + 1}" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>`
        : '';

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
        + `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
        + freeze
        + (cols ? `<cols>${cols}</cols>` : '')
        + `<sheetData>${rows}</sheetData>`
        + `</worksheet>`;
}

function stylesXml(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
        + `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
        + `<numFmts count="1"><numFmt numFmtId="164" formatCode="#,##0.00"/></numFmts>`
        + `<fonts count="5">`
        + `<font><sz val="11"/><name val="Calibri"/></font>`
        + `<font><b/><sz val="11"/><name val="Calibri"/></font>`
        + `<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>`
        + `<font><b/><sz val="16"/><color rgb="FF912721"/><name val="Calibri"/></font>`
        + `<font><i/><sz val="10"/><color rgb="FF6B7280"/><name val="Calibri"/></font>`
        + `</fonts>`
        + `<fills count="3">`
        + `<fill><patternFill patternType="none"/></fill>`
        + `<fill><patternFill patternType="gray125"/></fill>`
        + `<fill><patternFill patternType="solid"><fgColor rgb="FF912721"/><bgColor indexed="64"/></patternFill></fill>`
        + `</fills>`
        + `<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>`
        + `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>`
        + `<cellXfs count="10">`
        // 0 normal
        + `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>`
        // 1 negrita
        + `<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>`
        // 2 cabecera de tabla: blanco sobre vino, centrado
        + `<xf numFmtId="0" fontId="2" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>`
        // 3 moneda
        + `<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`
        // 4 moneda en negrita (totales)
        + `<xf numFmtId="164" fontId="1" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/>`
        // 5 título de hoja
        + `<xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1"/>`
        // 6 porcentaje
        + `<xf numFmtId="9" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`
        // 7 texto secundario
        + `<xf numFmtId="0" fontId="4" fillId="0" borderId="0" xfId="0" applyFont="1"/>`
        // 8 entero con separador de miles
        + `<xf numFmtId="3" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`
        // 9 entero en negrita
        + `<xf numFmtId="3" fontId="1" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/>`
        + `</cellXfs>`
        + `<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>`
        + `</styleSheet>`;
}

/** Arma el .xlsx (bytes) a partir de las hojas. */
export function buildXlsx(sheets: XSheet[], when: Date = new Date()): Uint8Array {
    const names = sheets.map(s => sheetName(s.name));

    const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
        + `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
        + `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>`
        + `<Default Extension="xml" ContentType="application/xml"/>`
        + `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>`
        + `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>`
        + sheets.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')
        + `</Types>`;

    const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
        + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
        + `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>`
        + `</Relationships>`;

    const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
        + `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
        + `<sheets>`
        + names.map((n, i) => `<sheet name="${xmlEscape(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')
        + `</sheets></workbook>`;

    const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
        + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
        + sheets.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')
        + `<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>`
        + `</Relationships>`;

    const files = [
        { name: '[Content_Types].xml', data: utf8(contentTypes) },
        { name: '_rels/.rels', data: utf8(rootRels) },
        { name: 'xl/workbook.xml', data: utf8(workbook) },
        { name: 'xl/_rels/workbook.xml.rels', data: utf8(workbookRels) },
        { name: 'xl/styles.xml', data: utf8(stylesXml()) },
        ...sheets.map((sheet, i) => ({ name: `xl/worksheets/sheet${i + 1}.xml`, data: utf8(sheetXml(sheet)) })),
    ];
    return zipStore(files, when);
}

// ═══════════════════════════════════════════════════════════════════════════
//  HOJAS DEL INFORME
// ═══════════════════════════════════════════════════════════════════════════

const text = (v: string, s: number = XS.normal): XCell => ({ v, s });
const money = (v: number, bold: boolean = false): XCell => ({ v: Number(v || 0), s: bold ? XS.moneyBold : XS.money });
const int = (v: number, bold: boolean = false): XCell => ({ v: Number(v || 0), s: bold ? XS.intBold : XS.int });
const pct = (v: number): XCell => ({ v: Number(v || 0) / 100, s: XS.percent });
const blank: XCell[] = [];

/** Título, evento y datos de generación: encabezado común de cada hoja. */
function sheetHeading(report: EventReport, subtitle: string): XCell[][] {
    return [
        [text(`Informe de ventas del evento · ${subtitle}`, XS.title)],
        [text(report.event_name || 'Evento', XS.bold)],
        [text(
            `Evento: ${report.event_dates || 'sin fecha registrada'}`
            + (report.event_location ? ` · ${report.event_location}` : '')
            + ` · Moneda: ${report.currency_code}`
            + ` · Generado el ${formatDateTime(report.generated_at)}`
            + (report.generated_by ? ` por ${report.generated_by}` : '')
            + ` · Barlleno / TecWave © ${report.generated_at.getFullYear()}`,
            XS.muted,
        )],
        blank,
    ];
}

export function buildEventReportXlsx(report: EventReport): Uint8Array {
    const t = report.totals;

    // ── Resumen ──────────────────────────────────────────────────────────
    const summaryRows: XCell[][] = [
        ...sheetHeading(report, 'Resumen general'),
        [text('Indicador', XS.header), text('Valor', XS.header)],
        ...report.summary.map(row => {
            const value: XCell = row.kind === 'money' ? money(row.raw)
                : row.kind === 'count' ? int(row.raw)
                    : text(row.value);
            return [text(row.label, XS.bold), value];
        }),
        blank,
        [text('Fecha del evento', XS.bold), text(report.event_dates || 'Sin fecha registrada')],
        [text('Lugar', XS.bold), text(report.event_location || '—')],
        [text('Generado el', XS.bold), text(formatDateTime(report.generated_at))],
        [text('Generado por', XS.bold), text(report.generated_by || '—')],
    ];

    // ── Estaciones (solo los métodos de pago visibles) ───────────────────
    const methods = stationMethodColumns(report);
    const stationHead: XCell[] = [text('Estación', XS.header), text('Órdenes', XS.header)];
    methods.forEach(m => stationHead.push(text(m.label, XS.header)));
    if (report.show_cost_admin) stationHead.push(text('Cargo boletería', XS.header));
    stationHead.push(text('Total', XS.header), text('Participación', XS.header));

    const stationRows: XCell[][] = report.stations.map(s => {
        const row: XCell[] = [text(s.name), int(s.orders)];
        methods.forEach(m => row.push(money(s[m.field])));
        if (report.show_cost_admin) row.push(money(s.cost_admin));
        row.push(money(s.total, true), pct(s.pct));
        return row;
    });
    const stationTotals: XCell[] = [text('Total del evento', XS.bold), int(t.orders, true)];
    methods.forEach(m => stationTotals.push(money(t[m.field], true)));
    if (report.show_cost_admin) stationTotals.push(money(t.cost_admin, true));
    stationTotals.push(money(t.sales, true), pct(100));

    const stationCols = [30, 12];
    methods.forEach(() => stationCols.push(14));
    if (report.show_cost_admin) stationCols.push(16);
    stationCols.push(16, 14);

    // ── Cajeros por estación ─────────────────────────────────────────────
    const cashierHead: XCell[] = [text('Estación', XS.header), text('Cajero', XS.header), text('Correo', XS.header), text('Situación', XS.header), text('Órdenes', XS.header)];
    methods.forEach(m => cashierHead.push(text(m.label, XS.header)));
    cashierHead.push(text('Total', XS.header));
    const cashierRows: XCell[][] = [];
    report.stations.filter(s => s.cashiers && s.cashiers.length > 0).forEach(s => {
        s.cashiers.forEach(c => {
            const situation = c.no_sales ? 'Sin ventas' : (!c.assigned ? (c.assigned_to ? `Asignado a ${c.assigned_to}` : 'Sin asignar') : 'Asignado');
            const row: XCell[] = [text(s.name), text(c.name), text(c.email || ''), text(situation), int(c.orders)];
            methods.forEach(m => row.push(money(c[m.field])));
            row.push(money(c.total));
            cashierRows.push(row);
        });
        const sub: XCell[] = [text(`Total ${s.name}`, XS.bold), text('', XS.bold), text(''), text(''), int(s.orders, true)];
        methods.forEach(m => sub.push(money(s[m.field], true)));
        sub.push(money(s.total, true));
        cashierRows.push(sub);
    });
    const cashierCols = [24, 26, 30, 20, 12];
    methods.forEach(() => cashierCols.push(14));
    cashierCols.push(16);

    // ── Métodos de pago ──────────────────────────────────────────────────
    const payRows: XCell[][] = report.payments.map(p => [text(p.label), int(p.count), money(p.total, true), pct(p.pct)]);

    // ── Productos ────────────────────────────────────────────────────────
    const prodHead: XCell[] = [text('Producto', XS.header)];
    if (report.ticketing) prodHead.push(text('Tipo', XS.header));
    prodHead.push(text('Cantidad', XS.header), text('Ingreso', XS.header));

    const prodRows: XCell[][] = report.products.map(p => {
        const row: XCell[] = [text(p.name)];
        if (report.ticketing) row.push(text(p.ticket ? 'Entrada' : 'Producto'));
        row.push(int(p.quantity), money(p.total));
        return row;
    });
    const prodTotals: XCell[] = [text('Total', XS.bold)];
    if (report.ticketing) prodTotals.push(text(''));
    prodTotals.push(int(t.items, true), money(report.products.reduce((sum, p) => sum + Number(p.total || 0), 0), true));

    const prodCols = [44];
    if (report.ticketing) prodCols.push(12);
    prodCols.push(12, 16);

    // ── Productos por barra y por caja ───────────────────────────────────
    const zoneProdHead: XCell[] = [text('Barra', XS.header), text('Caja', XS.header), text('Producto', XS.header), text('Cantidad', XS.header), text('Ingreso', XS.header)];
    const zoneProdRows: XCell[][] = [];
    (report.station_products || []).forEach(z => {
        z.products.forEach(pr => zoneProdRows.push([text(z.name), text('Toda la barra'), text(pr.name), int(pr.quantity), money(pr.total)]));
        zoneProdRows.push([text(`Total ${z.name}`, XS.bold), text('', XS.bold), text('', XS.bold), int(z.units, true), money(z.total, true)]);
        z.cashiers.forEach(c => {
            c.products.forEach(pr => zoneProdRows.push([text(z.name), text(c.name), text(pr.name), int(pr.quantity), money(pr.total)]));
            zoneProdRows.push([text(z.name, XS.bold), text(`Total ${c.name}`, XS.bold), text('', XS.bold), int(c.units, true), money(c.total, true)]);
        });
    });

    // ── Regalías (órdenes con total 0) ───────────────────────────────────
    const courtesy = report.courtesy;
    const courtesyRows: XCell[][] = [];
    if (courtesy && courtesy.orders > 0) {
        courtesyRows.push(
            [text('Indicador', XS.header), text('Órdenes', XS.header), text('Unidades', XS.header)],
            [text('Vendido (con cobro)', XS.bold), int(courtesy.sold_orders), int(courtesy.sold_units)],
            [text('Regalía (sin cobro)', XS.bold), int(courtesy.orders), int(courtesy.units)],
            [text('Total entregado', XS.bold), int(courtesy.sold_orders + courtesy.orders, true), int(courtesy.sold_units + courtesy.units, true)],
            blank,
            [text('Regalías por empresa', XS.bold)],
            [text('Empresa', XS.header), text('Órdenes', XS.header), text('Unidades', XS.header), text('Qué se regaló', XS.header)],
            ...courtesy.companies.map(c => [text(c.company), int(c.orders), int(c.units), text(c.detail)]),
            [text('Total', XS.bold), int(courtesy.orders, true), int(courtesy.units, true), text('')],
            blank,
        );
        if (courtesy.products.length > 0) {
            courtesyRows.push(
                [text('Vendido contra regalado por producto', XS.bold)],
                [text('Producto', XS.header), text('Vendidas', XS.header), text('De regalía', XS.header), text('Total', XS.header)],
                ...courtesy.products.map(p => [text(p.name), int(p.sold), int(p.courtesy), int(p.total, true)]),
                blank,
            );
        }
        courtesyRows.push(
            [text('Detalle de regalías', XS.bold)],
            [text('Orden', XS.header), text('Fecha', XS.header), text('Empresa', XS.header), text('Unidades', XS.header), text('Detalle', XS.header), text('Barra', XS.header), text('Cajero', XS.header)],
            ...courtesy.list.map(o => [text(o.number), text(o.date), text(o.company), int(o.units), text(o.detail), text(o.zone), text(o.cashier)]),
        );
    }

    const heading = 4; // filas del encabezado común
    return buildXlsx([
        { name: 'Resumen', cols: [34, 22], rows: summaryRows },
        {
            name: 'Por estación',
            cols: stationCols,
            rows: [...sheetHeading(report, 'Venta por estación'), stationHead, ...stationRows, stationTotals],
            freezeRow: heading + 1,
        },
        ...(cashierRows.length > 0 ? [{
            name: 'Cajeros',
            cols: cashierCols,
            rows: [...sheetHeading(report, 'Cajeros por estación'), cashierHead, ...cashierRows],
            freezeRow: heading + 1,
        }] : []),
        {
            name: 'Métodos de pago',
            cols: [26, 12, 16, 14],
            rows: [
                ...sheetHeading(report, 'Métodos de pago'),
                [text('Método de pago', XS.header), text('Órdenes', XS.header), text('Monto', XS.header), text('Participación', XS.header)],
                ...payRows,
                [text('Total cobrado', XS.bold), int(t.orders, true), money(t.sales, true), pct(100)],
            ],
            freezeRow: heading + 1,
        },
        {
            name: 'Productos',
            cols: prodCols,
            rows: [...sheetHeading(report, 'Detalle de lo vendido'), prodHead, ...prodRows, prodTotals],
            freezeRow: heading + 1,
        },
        ...(zoneProdRows.length > 0 ? [{
            name: 'Por barra y caja',
            cols: [26, 28, 44, 12, 16],
            rows: [...sheetHeading(report, 'Productos por barra y caja (solo quien vendió)'), zoneProdHead, ...zoneProdRows],
            freezeRow: heading + 1,
        }] : []),
        ...(courtesyRows.length > 0 ? [{
            name: 'Regalías',
            cols: [30, 18, 26, 12, 44, 22, 26],
            rows: [...sheetHeading(report, 'Regalías (órdenes con total 0: no suman a la venta)'), ...courtesyRows],
        }] : []),
    ], report.generated_at);
}

/** Fecha corta para nombres y celdas donde no cabe la hora. */
export function shortDate(d: Date): string {
    return formatDate(d);
}
